import { useCallback, useState } from 'react';

import { useMediaQuery } from './useMediaQuery';

const RAIL_STORAGE_KEY = 'fieldops.rail.expanded';

/** From this width the rail's words fit beside the work, so they are shown unless hidden by choice. */
const LABELS_MIN_WIDTH_PX = 1280;

function readChoice(): boolean | null {
  try {
    const saved = globalThis.localStorage.getItem(RAIL_STORAGE_KEY);
    if (saved === 'expanded') return true;
    if (saved === 'collapsed') return false;
  } catch {
    // Storage may be unavailable; follow the viewport.
  }
  return null;
}

/**
 * Whether the rail shows its labels.
 *
 * Wide viewports start expanded and narrow ones start collapsed, and the person's own toggle wins
 * over both from then on, across visits.
 *
 * @returns the state and the toggle that records the person's choice
 */
export function useRailExpanded(): { readonly expanded: boolean; readonly toggle: () => void } {
  const [choice, setChoice] = useState<boolean | null>(readChoice);
  const wide = useMediaQuery(`(min-width: ${String(LABELS_MIN_WIDTH_PX)}px)`);
  const expanded = choice ?? wide;

  const toggle = useCallback(() => {
    const next = !expanded;
    setChoice(next);
    try {
      globalThis.localStorage.setItem(RAIL_STORAGE_KEY, next ? 'expanded' : 'collapsed');
    } catch {
      // Storage may be unavailable; the choice still applies for this session.
    }
  }, [expanded]);

  return { expanded, toggle };
}
